// A FINALISED QUOTE'S OUTCOME on the quote screen (quote-won-lost, owner
// 2026-09-23): Open / Won / Lost, with Expired shown for an Open quote past
// its Valid Until.
//
// ⚠️ THE PANEL IS NOT THE GUARD. The backend refuses a draft, a quote the
// caller does not own (Sales), a Viewer, a Lost without a reason from the
// list, an Other without a note, and a reset by anyone but an Admin
// (test_quote_outcome.py). This renders what the backend reported and offers
// the actions the signed-in role may take.
//
// ⚠️ A STATUS CHANGE MOVES NO FIGURE, and nothing on this panel reaches the
// customer document.

import { useEffect, useState } from "react";
import type { QuoteOutcome } from "@/types";
import { LOST_REASONS, type CustomerDateEntry } from "@/services/api";
import { WonDatesForm } from "@/components/CustomerDates";
import { StatusPill } from "@/components/StatusPill";
import { formatDate } from "@/lib/format";
import { colors, semanticColors, radius, spacing, heights, typography } from "@/styles/tokens";

const BAR: React.CSSProperties = {
  marginTop: spacing[12],
  padding: `${spacing[10]}px ${spacing[12]}px`,
  backgroundColor: semanticColors.panelBg,
  border: `1px solid ${semanticColors.border}`,
  color: semanticColors.textSecondary,
  borderRadius: radius.base,
  fontSize: typography.fontSize.sm,
  display: "flex", alignItems: "center", gap: spacing[12], flexWrap: "wrap",
};

const BTN: React.CSSProperties = {
  height: heights.control, padding: `0 ${spacing[12]}px`,
  backgroundColor: semanticColors.surface, color: semanticColors.textSecondary,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium, cursor: "pointer",
};

const PRIMARY: React.CSSProperties = {
  ...BTN, backgroundColor: colors.indigo600, color: semanticColors.onAccent, border: "none",
};

const PILL_FOR = {
  open: "Open", won: "Won", lost: "Lost", expired: "Expired", superseded: "Superseded",
} as const;

export function OutcomePanel({
  outcome, canMark, isAdmin, busy, error, onWon, onLost, onReset, quoteId, openWon = 0, openLost = 0,
}: {
  outcome: QuoteOutcome;
  /** Sales on their own quote, or an Admin. A Viewer sees the status only. */
  canMark: boolean;
  isAdmin: boolean;
  busy: boolean;
  error: string;
  /** M3.1 (B3): the dialog's customer dates; [] = every line not stated. */
  onWon: (customerDates: CustomerDateEntry[]) => void;
  onLost: (reason: string, note: string) => void;
  onReset: () => void;
  /** The saved quote; with it, Mark won first asks for customer dates (optional). */
  quoteId?: number;
  /** ux-guided-deal: bumped by the next-step card to open the Won / Lost form. */
  openWon?: number;
  openLost?: number;
}) {
  const [losing, setLosing] = useState(false);
  const [winning, setWinning] = useState(false);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const status = outcome.status;
  // A recorded change closes the Lost form, so a later reset opens clean.
  useEffect(() => { setLosing(false); setWinning(false); setReason(""); setNote(""); }, [status]);
  // The card's button opens the SAME form the panel's own button opens.
  useEffect(() => {
    if (openWon > 0 && canMark) { setLosing(false); if (quoteId === undefined) onWon([]); else setWinning(true); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openWon]);
  useEffect(() => { if (openLost > 0 && canMark) { setWinning(false); setLosing(true); } }, [openLost, canMark]);
  if (!status) return null;

  const undecided = status === "open" || status === "expired";
  const noteNeeded = reason === "other" && !note.trim();

  return (
    <div data-testid="outcome-panel" data-status={status} style={BAR}>
      <span style={{ fontWeight: typography.fontWeight.medium, color: semanticColors.textPrimary }}>
        Outcome
      </span>
      <StatusPill status={PILL_FOR[status]} />
      {status === "expired" && (
        <span data-testid="outcome-expired-note">Past its Valid Until date, no outcome recorded.</span>
      )}
      {status === "superseded" && (
        <span data-testid="outcome-superseded-by">
          Superseded by {outcome.supersededBy?.name ?? "a later revision"}. Outcome and PO work happen there.
        </span>
      )}
      {status === "lost" && (
        <span data-testid="outcome-lost-reason">
          {outcome.lostReasonLabel ?? "Lost"}
          {outcome.lostNote ? ` — ${outcome.lostNote}` : ""}
        </span>
      )}
      {(status === "won" || status === "lost") && outcome.changedBy && (
        <span data-testid="outcome-changed">
          by {outcome.changedBy}
          {outcome.changedAt ? `, ${formatDate(outcome.changedAt.slice(0, 10))}` : ""}
        </span>
      )}

      {canMark && undecided && !losing && !winning && (
        <>
          <button data-testid="outcome-mark-won" disabled={busy}
            onClick={() => { if (quoteId === undefined) onWon([]); else setWinning(true); }} style={PRIMARY}>
            Mark won
          </button>
          <button data-testid="outcome-mark-lost" disabled={busy} onClick={() => setLosing(true)} style={BTN}>
            Mark lost
          </button>
        </>
      )}
      {canMark && undecided && winning && quoteId !== undefined && (
        <WonDatesForm quoteId={quoteId} busy={busy} onConfirm={onWon} onCancel={() => setWinning(false)} />
      )}
      {canMark && undecided && losing && (
        <>
          <select
            data-testid="outcome-lost-reason-select"
            aria-label="Lost reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            style={{ ...BTN, color: semanticColors.textPrimary }}
          >
            <option value="">Reason (required)</option>
            {LOST_REASONS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
          <input
            data-testid="outcome-lost-note"
            aria-label="Lost note"
            value={note}
            placeholder={reason === "other" ? "Note (required for Other)" : "Note (optional)"}
            onChange={(e) => setNote(e.target.value)}
            style={{ ...BTN, cursor: "text", minWidth: 220, color: semanticColors.textPrimary }}
          />
          <button
            data-testid="outcome-lost-confirm"
            disabled={busy || !reason || noteNeeded}
            onClick={() => onLost(reason, note.trim())}
            style={BTN}
          >
            Mark lost
          </button>
          <button
            data-testid="outcome-lost-cancel"
            onClick={() => { setLosing(false); setReason(""); setNote(""); }}
            style={BTN}
          >
            Cancel
          </button>
        </>
      )}
      {isAdmin && (status === "won" || status === "lost") && (
        <button data-testid="outcome-reset" disabled={busy} onClick={onReset} style={BTN}>
          Reset to open
        </button>
      )}
      {error && (
        <div data-testid="outcome-error" role="alert" style={{ width: "100%", color: semanticColors.destructive }}>
          {error}
        </div>
      )}
    </div>
  );
}
